import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, ArrowRight, GitBranch, Flag, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const NEW_SECTION = "__new_section__";
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random());

// Per-section branching choices editor. Reused in the Manuscript and Blueprint views.
export default function ChoicesEditor({ chapter, chapters, onUpdate, onCreateSection, onOpenChapter, compact = false }) {
  const choices = Array.isArray(chapter.choices) ? chapter.choices : [];
  const [busyId, setBusyId] = useState(null);
  const others = chapters.filter((c) => c.id !== chapter.id);

  const commit = (next) => onUpdate({ choices: next });
  const addChoice = () => commit([...choices, { id: uid(), label: "", target_id: null }]);
  const updateChoice = (id, patch) => commit(choices.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const removeChoice = (id) => commit(choices.filter((c) => c.id !== id));

  const setTarget = async (choice, val) => {
    if (val === NEW_SECTION) {
      setBusyId(choice.id);
      try {
        const title = choice.label.trim() || "New section";
        const created = await onCreateSection(title, chapter.act_id);
        // re-read latest to avoid clobbering concurrent edits
        commit(choices.map((c) => (c.id === choice.id ? { ...c, target_id: created.id } : c)));
        toast.success(`Created "${created.title}" and linked it`);
      } catch {
        toast.error("Couldn't create the section");
      } finally {
        setBusyId(null);
      }
    } else {
      updateChoice(choice.id, { target_id: val });
    }
  };

  return (
    <div data-testid={`choices-editor-${chapter.id}`} className="space-y-2.5">
      <div className="flex items-center gap-2">
        <GitBranch className="w-4 h-4 text-primary" />
        <span className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">
          Choices {choices.length > 0 && <span className="text-foreground">({choices.length})</span>}
        </span>
      </div>

      {choices.length === 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-dashed border-border bg-secondary/30 px-3 py-2 text-xs text-muted-foreground" data-testid={`section-ending-${chapter.id}`}>
          <Flag className="w-3.5 h-3.5 text-amber-500" />
          No choices yet — readers reach <span className="font-semibold text-foreground">THE END</span> here. Add a choice to branch the story.
        </div>
      )}

      <div className="space-y-2">
        {choices.map((choice, idx) => {
          const target = others.find((c) => c.id === choice.target_id);
          return (
            <div key={choice.id} data-testid={`choice-row-${chapter.id}-${idx}`}
              className="rounded-lg border border-border bg-card p-2.5 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono-code text-muted-foreground w-4 shrink-0">{idx + 1}.</span>
                <Input value={choice.label} onChange={(e) => updateChoice(choice.id, { label: e.target.value })}
                  placeholder="e.g. Open the door" data-testid={`choice-label-${chapter.id}-${idx}`}
                  className="h-8 flex-1" />
                <button onClick={() => removeChoice(choice.id)} data-testid={`choice-delete-${chapter.id}-${idx}`}
                  className="text-muted-foreground hover:text-destructive shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center gap-2 pl-6">
                <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <div className="flex-1">
                  <Select value={choice.target_id || ""} onValueChange={(v) => setTarget(choice, v)} disabled={busyId === choice.id}>
                    <SelectTrigger className="h-8" data-testid={`choice-target-${chapter.id}-${idx}`}>
                      {busyId === choice.id
                        ? <span className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Creating…</span>
                        : <SelectValue placeholder="Go to section…" />}
                    </SelectTrigger>
                    <SelectContent className="bg-popover max-h-64">
                      {others.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                      ))}
                      <SelectItem value={NEW_SECTION} data-testid={`choice-new-section-${chapter.id}-${idx}`} className="text-primary font-medium">
                        <span className="flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> New section…</span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {target && onOpenChapter && (
                  <button onClick={() => onOpenChapter(target.id)} title={`Open ${target.title}`}
                    data-testid={`choice-open-target-${chapter.id}-${idx}`}
                    className="text-muted-foreground hover:text-primary shrink-0">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Button variant="outline" size="sm" onClick={addChoice} data-testid={`add-choice-${chapter.id}`}
        className="w-full gap-1.5 border-dashed">
        <Plus className="w-4 h-4" /> Add choice
      </Button>
    </div>
  );
}
